export enum DrawingToolType {
    ARROW = 'arrow',
    CIRCLE = 'circle',
    RECTANGLE = 'rectangle',
    FREE_DRAWING = 'free-drawing',
    TEXT = 'text',
    ERASER = 'eraser',
    HAND = 'hand',
}

/**
 * A minimum type to describe Konva.js shapes configs
 */
export interface ShapeConfig {
    // The type parameter is not used directly in Konva.js but is useful to generate annotations
    type: DrawingToolType

    // Common parameters
    id: string
    fill: string
    stroke: string
    strokeWidth: number
    x: number
    y: number

    // parameters that can be used in the different shape types
    radius?: number
    width?: number
    height?: number
    points?: number[]
    opacity?: number
}

/**
 * To create a drawing tool, override this class and implement/define the abstract methods and properties
 * Then link the tool to an editor button in the method 'getTool' of the drawing-editor
 * component
 */
export abstract class DrawingTool {

    protected abstract readonly TOOL_TYPE: DrawingToolType;

    protected readonly DEFAULT_STROKE_COLOR = "black";
    protected readonly DEFAULT_FILL_COLOR = "transparent";
    protected readonly DEFAULT_OPACITY = 1;

    protected strokeWidth = 5;
    protected strokeColor = this.DEFAULT_STROKE_COLOR;

    constructor(strokeWidth: number, strokeColor?) {
        this.strokeWidth = strokeWidth;
        if (strokeColor) {
            this.strokeColor = strokeColor;
        }
    }

    public addShape(shapeX, shapeY, id, color?: any, parameters?: any): ShapeConfig {
        this.resetTool()
        return this.addNewShape(shapeX, shapeY, id, color, parameters)
    }

    /**
     * Override this method to modify the current shape during
     * an ontouchmove event
     * @param currentShape 
     * @param currentX 
     * @param currentY 
     * @param startX 
     * @param startY 
     */
    public abstract onTouchMove(currentShape, currentX, currentY, startX, startY, debugShapes?): void

    /**
     * Override this method to make one last modification to 
     * the current shape. This corresponds to the end of the drawing (touchend event)
     * @param currentShape 
     * @param currentX 
     * @param currentY 
     * @param startX 
     * @param startY 
     */
    public abstract onTouchEnd(currentShape, currentX, currentY, startX, startY): void

    /**
     * If the tool has class members, override this method 
     * to reset it before adding a new shape
     */
    public abstract resetTool();

    public generateShapeId(id, index: number | undefined = undefined) {
        let result = `${this.TOOL_TYPE}${id}`
        return index ? result + `[${index}]` : result
    }

    /**
     * Override this method to return a new shape using the different parameters
     * @param shapeX 
     * @param shapeY 
     * @param id 
     * @param parameters 
     */
    protected abstract addNewShape(shapeX, shapeY, id, color?: string, parameters?: any): ShapeConfig;
}